import type { IntlShape } from 'react-intl';
import {
  STEP_STATUS_FAILED,
  STEP_STATUS_IN_PROGRESS,
  STEP_STATUS_PENDING,
  STEP_STATUS_SUCCEEDED,
  type Step,
  type StepStatus,
} from '@aibuild/ui';
import type { ToolExecution } from '@hooks/useChat';
import type { ScopedAssistantProgress } from '@components/ScopedAssistantChat';

export const DRAFT_WRITING_TOOLS: ReadonlySet<string> = new Set(['set_app_page', 'edit_app_page', 'update_app_draft']);

const STEP_MESSAGES: Readonly<Record<string, { id: string; defaultMessage: string }>> = {
  get_app_kit: { id: 'apps.builder.step.kit', defaultMessage: 'Reading the App Kit' },
  get_app_draft: { id: 'apps.builder.step.read', defaultMessage: 'Reading the app' },
  get_app_page: { id: 'apps.builder.step.read', defaultMessage: 'Reading the app' },
  list_available_skills: { id: 'apps.builder.step.skills', defaultMessage: 'Picking the skills' },
  list_available_models: { id: 'apps.builder.step.models', defaultMessage: 'Picking the model' },
  update_app_draft: { id: 'apps.builder.step.details', defaultMessage: 'Saving the app details' },
  set_app_page: { id: 'apps.builder.step.page', defaultMessage: 'Laying out the page' },
  edit_app_page: { id: 'apps.builder.step.section', defaultMessage: 'Writing a part of the page' },
  validate_app_draft: { id: 'apps.builder.step.check', defaultMessage: 'Checking everything works' },
  get_test_run_report: { id: 'apps.builder.step.testRun', defaultMessage: 'Reading the test run' },
  view_app_preview: { id: 'apps.builder.step.look', defaultMessage: 'Looking at the preview' },
  ask_user: { id: 'apps.builder.step.ask', defaultMessage: 'Asking you a question' },
};

const OTHER_STEP = { id: 'apps.builder.step.other', defaultMessage: 'Working' };

// Never the call's summary: without one the AI service falls back to the first argument, which for
// the builder's tools is the instructions or the page's HTML.
export const stepLabel = (intl: IntlShape, step: ToolExecution): string =>
  intl.formatMessage(STEP_MESSAGES[step.tool] ?? OTHER_STEP);

export const isStepDone = (step: ToolExecution): boolean => step.status === 'complete';

export interface BuildStatus {
  readonly building: boolean;
  readonly done: number;
  readonly current: ToolExecution | null;
}

/** A reconnect alone is not a build: it runs on every visit, and only counts while a step is still going. */
export const buildStatusOf = ({ sending, reconnecting, steps }: ScopedAssistantProgress): BuildStatus => {
  const running = steps.filter((step) => step.status !== 'complete');
  return {
    building: sending || (reconnecting && running.length > 0),
    done: steps.filter(isStepDone).length,
    current: running.at(-1) ?? steps.at(-1) ?? null,
  };
};

const statusOf = (calls: readonly ToolExecution[]): StepStatus => {
  if (calls.some((call) => call.status === 'executing')) return STEP_STATUS_IN_PROGRESS;
  if (calls.some((call) => call.status === 'pending_approval')) return STEP_STATUS_PENDING;
  return calls.at(-1)?.success === false ? STEP_STATUS_FAILED : STEP_STATUS_SUCCEEDED;
};

/** One row per thing the builder does, however often it does it, ticked once every call of it is done. */
export const stepListOf = (intl: IntlShape, steps: readonly ToolExecution[]): Step[] => {
  const byLabel = new Map<string, ToolExecution[]>();
  for (const step of steps) {
    const label = stepLabel(intl, step);
    byLabel.set(label, [...(byLabel.get(label) ?? []), step]);
  }
  return [...byLabel].map(([title, calls]) => ({ id: title, title, status: statusOf(calls) }));
};

export const draftWritesOf = (steps: readonly ToolExecution[]): string[] =>
  steps.flatMap((step, index) =>
    DRAFT_WRITING_TOOLS.has(step.tool) && isStepDone(step) && step.success !== false
      ? [step.toolUseId ?? `${step.tool}-${index}`]
      : [],
  );
